import React, { useState } from 'react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { CallbackModal } from '../../components/CallbackModal';
import { Calendar, Clock, Tag, ChevronLeft, MessageCircle, Plus, Minus, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-1068">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-1069">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-1070">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-1071">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-1072" /> : <Plus className="w-4 h-4" data-id="element-1073" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-1074">
        <p className="text-gray-600 leading-relaxed" data-id="element-1075">{answer}</p>
      </div>
    </div>;
}
export function WithoutInsuranceArticle() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Чи потрібна страховка для візиту до стоматолога?',
    a: "Ні, у Великобританії стоматологічна страховка не є обов'язковою. Ви можете просто записатися на прийом і оплатити його (pay-as-you-go)."
  }, {
    q: 'Скільки коштує візит без страховки?',
    a: 'У приватних клініках огляд коштує від £40 до £100. Лікування оплачується окремо згідно з прайсом.'
  }, {
    q: 'Чи варто купувати стоматологічну страховку?',
    a: 'Це залежить від стану ваших зубів. Страховка може бути вигідною, якщо вам потрібне регулярне складне лікування, але часто простіше оплачувати візити по факту.'
  }, {
    q: 'Чи приймає Dentico Clinic страховки?',
    a: 'Ми працюємо як pay-as-you-go клініка, але надаємо всі необхідні документи (інвойси), щоб ви могли отримати відшкодування від вашої страхової компанії, якщо вона у вас є.'
  }, {
    q: 'Як зареєструватися в NHS без страховки?',
    a: 'Для реєстрації в NHS стоматолога страховка не потрібна. Вам просто потрібно знайти клініку, яка приймає нових пацієнтів, і заповнити форму.'
  }];
  // Schemas
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: 'Чи можна лікувати зуби в UK без страховки? Гід для українців',
    datePublished: '2026-02-12',
    author: {
      '@type': 'Organization',
      name: 'Dentico Clinic'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Dentico Clinic',
      logo: {
        '@type': 'ImageObject',
        url: 'https://denticoclinic.co.uk/logo.png'
      }
    },
    description: 'Дізнайтеся, як лікувати зуби в Лондоні без страховки. Поради щодо NHS, приватних клінік та оплати лікування.',
    inLanguage: 'uk'
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  return <Layout data-id="element-1076">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-1077" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(articleSchema)
    }} data-id="element-1078" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-1079" />

      <article className="bg-white" data-id="element-1080">
        {/* Header */}
        <div className="bg-teal-50 py-12 lg:py-20" data-id="element-1081">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1082">
            <Link to="/ua/blog" className="inline-flex items-center text-teal-600 font-medium mb-6 hover:underline" data-id="element-1083">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-1084" />
              Назад до блогу
            </Link>

            <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600" data-id="element-1085">
              <span className="bg-white px-3 py-1 rounded-full font-medium text-teal-700 flex items-center shadow-sm" data-id="element-1086">
                <Tag className="w-3 h-3 mr-2" data-id="element-1087" />
                Поради
              </span>
              <span className="flex items-center" data-id="element-1088">
                <Calendar className="w-4 h-4 mr-2" data-id="element-1089" />
                Лютий 2026
              </span>
              <span className="flex items-center" data-id="element-1090">
                <Clock className="w-4 h-4 mr-2" data-id="element-1091" />7 хв читання
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-1092">
              Чи можна лікувати зуби в UK без страховки? Гід для українців
            </h1>

            <div className="flex items-center" data-id="element-1093">
              <div className="w-10 h-10 bg-teal-200 rounded-full flex items-center justify-center text-teal-700 font-bold mr-3" data-id="element-1094">
                DC
              </div>
              <div data-id="element-1095">
                <p className="font-medium text-gray-900" data-id="element-1096">Dentico Clinic Team</p>
                <p className="text-sm text-gray-500" data-id="element-1097">Експертна стаття</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-1098">
          <div className="prose prose-lg prose-teal max-w-none text-gray-600" data-id="element-1099">
            <p className="lead text-xl text-gray-800 font-medium mb-8" data-id="element-1100">
              Багато українців, які приїжджають до Великобританії, звикли до
              того, що медицина тут страхова. Але чи стосується це стоматології?
              Чи можна піти до лікаря, якщо у вас немає полісу? Коротка
              відповідь: ТАК. Розповідаємо, як це працює.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-1101">
              Стоматологія в UK: Страховка не обов'язкова
            </h2>
            <p className="mb-6" data-id="element-1102">
              На відміну від США чи деяких країн Європи, у Великобританії доступ
              до стоматології не залежить від наявності страховки. Більшість
              британців лікують зуби за принципом{' '}
              <strong data-id="element-1103">"Pay-as-you-go"</strong> (оплата за фактом візиту).
            </p>
            <p className="mb-6" data-id="element-1104">
              Це стосується як державної (NHS), так і приватної стоматології.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-1105">
              Як лікуватися в NHS без страховки?
            </h2>
            <p className="mb-6" data-id="element-1106">
              NHS стоматологія доступна для всіх резидентів Великобританії
              (включаючи українців з візами BRP). Вам не потрібна страховка, щоб
              зареєструватися.
            </p>
            <p className="mb-6" data-id="element-1107">
              <strong data-id="element-1108">Як це працює:</strong>
            </p>
            <ol className="list-decimal pl-6 space-y-2 mb-8" data-id="element-1109">
              <li data-id="element-1110">
                Знайдіть клініку з контрактом NHS (це найскладніший етап, бо
                місць мало).
              </li>
              <li data-id="element-1111">
                Зателефонуйте і запитайте, чи приймають вони нових NHS
                пацієнтів.
              </li>
              <li data-id="element-1112">Запишіться на прийом.</li>
              <li data-id="element-1113">
                Оплатіть фіксовану вартість (Band 1, 2 або 3) в реєстратурі.
              </li>
            </ol>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-1114">
              Приватна стоматологія без страховки
            </h2>
            <p className="mb-6" data-id="element-1115">
              Приватні клініки, такі як Dentico Clinic, приймають всіх
              пацієнтів. Вам не потрібна ні реєстрація в NHS, ні страховка.
            </p>
            <p className="mb-6" data-id="element-1116">
              <strong data-id="element-1117">Переваги:</strong>
            </p>
            <ul className="space-y-3 mb-8" data-id="element-1118">
              <li className="flex items-start" data-id="element-1119">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-1120" />
                <span data-id="element-1121">Ви платите лише за те, що вам зробили.</span>
              </li>
              <li className="flex items-start" data-id="element-1122">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-1123" />
                <span data-id="element-1124">
                  Немає щомісячних платежів, якщо ви не користуєтесь послугами.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1125">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-1126" />
                <span data-id="element-1127">Прозорі ціни (ви бачите прайс-лист).</span>
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-1128">
              Що таке Dental Plans (Стоматологічні плани)?
            </h2>
            <p className="mb-6" data-id="element-1129">
              Деякі клініки пропонують власні "плани" (наприклад, Denplan). Ви
              платите фіксовану суму щомісяця (наприклад, £20-£30), і це
              покриває огляди та гігієну.
            </p>
            <p className="mb-6" data-id="element-1130">
              <strong data-id="element-1131">Чи варто це вам?</strong>
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-8" data-id="element-1132">
              <li data-id="element-1133">
                <strong data-id="element-1134">Так</strong>, якщо ви регулярно ходите на чистку і
                хочете розподілити витрати.
              </li>
              <li data-id="element-1135">
                <strong data-id="element-1136">Ні</strong>, якщо ви відвідуєте лікаря раз на рік або
                рідше.
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-1137">
              Як Dentico Clinic допомагає українцям?
            </h2>
            <p className="mb-6" data-id="element-1138">
              Ми розуміємо, що система може бути новою для вас. У Dentico
              Clinic:
            </p>
            <ul className="space-y-3 mb-8" data-id="element-1139">
              <li className="flex items-start" data-id="element-1140">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-1141" />
                <span data-id="element-1142">Ми приймаємо без страховки.</span>
              </li>
              <li className="flex items-start" data-id="element-1143">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-1144" />
                <span data-id="element-1145">
                  Ми надаємо детальний план лікування з цінами ПЕРЕД початком
                  процедур.
                </span>
              </li>
              <li className="flex items-start" data-id="element-1146">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-1147" />
                <span data-id="element-1148">
                  Якщо у вас Є страховка (наприклад, від роботодавця), ми надамо
                  всі документи для відшкодування.
                </span>
              </li>
            </ul>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 pt-10 border-t border-gray-100" data-id="element-1149">
            <h3 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-1150">
              Часті запитання
            </h3>
            <div className="space-y-2" data-id="element-1151">
              {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-1152" />)}
            </div>
          </div>

          {/* Read Also */}
          <div className="mt-16" data-id="element-1153">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-1154">
              Читайте також
            </h3>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-1155">
              <Link to="/ua/blog/nhs-vs-private-dentistry-uk" data-id="element-1156">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-1157">
                  <span className="text-teal-600 font-medium" data-id="element-1158">
                    NHS vs Приватна стоматологія
                  </span>
                </Card>
              </Link>
              <Link to="/ua/prices" data-id="element-1159">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-1160">
                  <span className="text-teal-600 font-medium" data-id="element-1161">Наші ціни</span>
                </Card>
              </Link>
            </div>
          </div>
        </div>

        {/* CTA */}
        <section className="py-16 bg-teal-900" data-id="element-1162">
          <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-1163">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1164">
              Запишіться на прийом без зайвих формальностей
            </h2>
            <p className="text-xl text-teal-100 mb-8 max-w-2xl mx-auto" data-id="element-1165">
              Проста реєстрація, прозорі ціни та якісне лікування.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-1166">
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1167">
                <Button size="lg" className="w-full gap-2 bg-white text-teal-900 hover:bg-teal-50" data-id="element-1168">
                  <Calendar className="w-5 h-5" data-id="element-1169" />
                  Записатись онлайн
                </Button>
              </a>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-1170">
                <Button variant="outline" size="lg" className="w-full gap-2 border-teal-400 text-teal-100 hover:bg-teal-800 hover:text-white" data-id="element-1171">
                  <MessageCircle className="w-5 h-5" data-id="element-1172" />
                  WhatsApp
                </Button>
              </a>
            </div>
          </div>
        </section>
      </article>
    </Layout>;
}